import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { convertScoreToFive } from '@/lib/utils';

interface Analyse {
  id: string;
  depot_id: string;
  ao_id: string;
  resultat_json: any;
  overall_score: number;
  decision: 'shortlist' | 'consider' | 'reject';
  tokens_in: number;
  tokens_out: number;
  cost_estimate: number;
  created_at: string;
  depots: {
    candidats: {
      nom: string;
      societes: {
        nom: string;
      };
    };
    appels_offres: {
      titre: string;
      reference: string;
    };
    texte_extrait: string;
    fichier_file_id: string;
  };
}

interface Question {
  question: string;
  critere_associe: string;
  objectif: string;
}

export const useAnalysisPdfExport = () => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDecisionLabel = (decision: string) => {
    switch (decision) {
      case 'shortlist': return 'Shortlist';
      case 'consider': return 'À considérer';
      case 'reject': return 'Rejeté';
      default: return 'Inconnu';
    }
  };

  const getDecisionColor = (decision: string): [number, number, number] => {
    switch (decision) {
      case 'shortlist': return [34, 197, 94]; // green
      case 'consider': return [234, 179, 8]; // yellow
      case 'reject': return [239, 68, 68]; // red
      default: return [156, 163, 175]; // gray
    }
  };

  const extractFilename = (filePath?: string) => {
    if (!filePath) return 'Fichier inconnu';
    const parts = filePath.split('/');
    const filename = parts[parts.length - 1];
    const match = filename.match(/\d+[A-Z]?-(.+)/);
    return match ? match[1] : filename;
  };

  const ensureArray = (item: any): string[] => {
    if (Array.isArray(item)) return item.filter(i => typeof i === 'string');
    if (item === null || item === undefined) return [];
    if (typeof item === 'string') return [item];
    return [];
  };

  const drawRadarChart = (
    doc: jsPDF,
    centerX: number,
    centerY: number,
    radius: number,
    data: { name: string; score: number }[]
  ) => {
    if (data.length < 3) return;

    const angleStep = (2 * Math.PI) / data.length;
    const levels = 5; // Number of grid levels (0, 20, 40, 60, 80, 100)

    // Draw colored zone backgrounds (red < 40, yellow 40-70, green > 70)
    // Green zone (70-100)
    doc.setFillColor(220, 252, 231); // green-100
    for (let i = 0; i < data.length; i++) {
      const angle1 = i * angleStep - Math.PI / 2;
      const angle2 = ((i + 1) % data.length) * angleStep - Math.PI / 2;
      const outerRadius = radius;
      const innerRadius = radius * 0.7;
      
      doc.triangle(
        centerX + innerRadius * Math.cos(angle1), centerY + innerRadius * Math.sin(angle1),
        centerX + outerRadius * Math.cos(angle1), centerY + outerRadius * Math.sin(angle1),
        centerX + outerRadius * Math.cos(angle2), centerY + outerRadius * Math.sin(angle2),
        'F'
      );
      doc.triangle(
        centerX + innerRadius * Math.cos(angle1), centerY + innerRadius * Math.sin(angle1),
        centerX + outerRadius * Math.cos(angle2), centerY + outerRadius * Math.sin(angle2),
        centerX + innerRadius * Math.cos(angle2), centerY + innerRadius * Math.sin(angle2),
        'F'
      );
    }

    // Yellow zone (40-70)
    doc.setFillColor(254, 249, 195); // yellow-100
    for (let i = 0; i < data.length; i++) {
      const angle1 = i * angleStep - Math.PI / 2;
      const angle2 = ((i + 1) % data.length) * angleStep - Math.PI / 2;
      const outerRadius = radius * 0.7;
      const innerRadius = radius * 0.4;
      
      doc.triangle(
        centerX + innerRadius * Math.cos(angle1), centerY + innerRadius * Math.sin(angle1),
        centerX + outerRadius * Math.cos(angle1), centerY + outerRadius * Math.sin(angle1),
        centerX + outerRadius * Math.cos(angle2), centerY + outerRadius * Math.sin(angle2),
        'F'
      );
      doc.triangle(
        centerX + innerRadius * Math.cos(angle1), centerY + innerRadius * Math.sin(angle1),
        centerX + outerRadius * Math.cos(angle2), centerY + outerRadius * Math.sin(angle2),
        centerX + innerRadius * Math.cos(angle2), centerY + innerRadius * Math.sin(angle2),
        'F'
      );
    }

    // Red zone (0-40)
    doc.setFillColor(254, 226, 226); // red-100
    for (let i = 0; i < data.length; i++) {
      const angle1 = i * angleStep - Math.PI / 2;
      const angle2 = ((i + 1) % data.length) * angleStep - Math.PI / 2;
      const outerRadius = radius * 0.4;
      
      doc.triangle(
        centerX, centerY,
        centerX + outerRadius * Math.cos(angle1), centerY + outerRadius * Math.sin(angle1),
        centerX + outerRadius * Math.cos(angle2), centerY + outerRadius * Math.sin(angle2),
        'F'
      );
    }

    // Draw grid circles
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.2);
    for (let level = 1; level <= levels; level++) {
      const levelRadius = (radius * level) / levels;
      const points: [number, number][] = [];
      
      for (let i = 0; i < data.length; i++) {
        const angle = i * angleStep - Math.PI / 2;
        points.push([
          centerX + levelRadius * Math.cos(angle),
          centerY + levelRadius * Math.sin(angle)
        ]);
      }
      
      // Draw polygon for this level
      for (let i = 0; i < points.length; i++) {
        const next = (i + 1) % points.length;
        doc.line(points[i][0], points[i][1], points[next][0], points[next][1]);
      }
    }

    // Draw axis lines
    doc.setDrawColor(180, 180, 180);
    for (let i = 0; i < data.length; i++) {
      const angle = i * angleStep - Math.PI / 2;
      const endX = centerX + radius * Math.cos(angle);
      const endY = centerY + radius * Math.sin(angle);
      doc.line(centerX, centerY, endX, endY);
    }

    // Draw data polygon
    const dataPoints: [number, number][] = [];
    for (let i = 0; i < data.length; i++) {
      const angle = i * angleStep - Math.PI / 2;
      const value = (data[i].score / 100) * radius;
      dataPoints.push([
        centerX + value * Math.cos(angle),
        centerY + value * Math.sin(angle)
      ]);
    }

    // Draw filled area with semi-transparent blue
    doc.setFillColor(59, 130, 246);
    for (let i = 0; i < dataPoints.length; i++) {
      const next = (i + 1) % dataPoints.length;
      // Draw lighter fill
      doc.setFillColor(147, 197, 253); // blue-300
      doc.triangle(
        centerX, centerY,
        dataPoints[i][0], dataPoints[i][1],
        dataPoints[next][0], dataPoints[next][1],
        'F'
      );
    }
    
    // Draw outline
    doc.setDrawColor(37, 99, 235); // blue-600
    doc.setLineWidth(1.2);
    for (let i = 0; i < dataPoints.length; i++) {
      const next = (i + 1) % dataPoints.length;
      doc.line(dataPoints[i][0], dataPoints[i][1], dataPoints[next][0], dataPoints[next][1]);
    }

    // Draw data points
    doc.setFillColor(37, 99, 235);
    for (const point of dataPoints) {
      doc.circle(point[0], point[1], 1.5, 'F');
    }

    // Draw labels
    doc.setFontSize(7);
    doc.setTextColor(60, 60, 60);
    doc.setFont('helvetica', 'normal');
    
    for (let i = 0; i < data.length; i++) {
      const angle = i * angleStep - Math.PI / 2;
      const labelRadius = radius + 8;
      const labelX = centerX + labelRadius * Math.cos(angle);
      const labelY = centerY + labelRadius * Math.sin(angle);
      
      // Truncate long names
      let labelText = data[i].name;
      if (labelText.length > 15) {
        labelText = labelText.substring(0, 14) + '...';
      }
      
      // Adjust alignment based on position
      let align: 'left' | 'center' | 'right' = 'center';
      let offsetX = 0;
      
      if (Math.cos(angle) > 0.3) {
        align = 'left';
        offsetX = 2;
      } else if (Math.cos(angle) < -0.3) {
        align = 'right';
        offsetX = -2;
      }
      
      doc.text(labelText, labelX + offsetX, labelY + 1, { align });
    }

    // Draw scale labels
    doc.setFontSize(6);
    doc.setTextColor(150, 150, 150);
    for (let level = 1; level <= levels; level++) {
      const levelRadius = (radius * level) / levels;
      const scoreValue = (level / levels) * 100;
      doc.text(String(Math.round(scoreValue)), centerX + 2, centerY - levelRadius + 2);
    }
  };

  const exportAnalysisToPdf = (analysis: Analyse, questions: Question[] = []) => {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    let yPos = margin;

    const addPageFooter = () => {
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text(`Page ${i}/${pageCount}`, pageWidth / 2, pageHeight - 8, { align: 'center' });
        doc.text(`Généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}`, pageWidth - margin, pageHeight - 8, { align: 'right' });
      }
    };

    const checkNewPage = (neededHeight: number) => {
      if (yPos + neededHeight > pageHeight - 20) {
        doc.addPage();
        yPos = margin;
        return true;
      }
      return false;
    };

    // === HEADER ===
    doc.setFillColor(59, 130, 246);
    doc.rect(0, 0, pageWidth, 35, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('Analyse de Candidature', margin, 18);
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text(extractFilename(analysis.depots.fichier_file_id), margin, 28);
    
    yPos = 45;

    // === INFORMATIONS CANDIDAT ===
    doc.setTextColor(59, 130, 246);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Informations du candidat', margin, yPos);
    yPos += 8;

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');

    const candidatInfo = [
      ['Candidat', analysis.depots.candidats.nom],
      ['Société', analysis.depots.candidats.societes.nom],
      ['Appel d\'offres', analysis.depots.appels_offres.titre],
      ['Référence AO', analysis.depots.appels_offres.reference],
      ['Date d\'analyse', formatDate(analysis.created_at)]
    ];

    autoTable(doc, {
      startY: yPos,
      head: [],
      body: candidatInfo,
      theme: 'plain',
      styles: { fontSize: 10, cellPadding: 2 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 40 },
        1: { cellWidth: 130 }
      },
      margin: { left: margin, right: margin }
    });

    yPos = (doc as any).lastAutoTable.finalY + 10;

    // === SCORE GLOBAL ===
    doc.setFillColor(240, 249, 255);
    doc.roundedRect(margin, yPos, pageWidth - 2 * margin, 35, 3, 3, 'F');

    doc.setTextColor(59, 130, 246);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Score Global', margin + 5, yPos + 10);

    // Score
    doc.setFontSize(28);
    doc.setTextColor(0, 0, 0);
    doc.text(`${analysis.overall_score}/100`, margin + 5, yPos + 27);
    
    doc.setFontSize(12);
    doc.setTextColor(100, 100, 100);
    doc.text(`(${convertScoreToFive(analysis.overall_score)}/5)`, margin + 50, yPos + 27);

    // Décision badge
    const decisionColor = getDecisionColor(analysis.decision);
    const decisionLabel = getDecisionLabel(analysis.decision);
    const decisionX = pageWidth - margin - 50;
    
    doc.setFillColor(...decisionColor);
    doc.roundedRect(decisionX, yPos + 12, 45, 12, 2, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(decisionLabel, decisionX + 22.5, yPos + 20, { align: 'center' });

    yPos += 45;

    // === RADAR CHART ===
    if (analysis.resultat_json.criteria_analyses && analysis.resultat_json.criteria_analyses.length >= 3) {
      checkNewPage(90);
      
      doc.setTextColor(59, 130, 246);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Vue d\'ensemble des scores par critère', margin, yPos);
      yPos += 8;

      const radarData = analysis.resultat_json.criteria_analyses.map((c: any) => ({
        name: c.criterion_name || 'Critère',
        score: c.criterion_score || 0,
        weight: c.weighting || c.criterion_weight || 0
      }));

      // Position radar on left side
      const radarCenterX = margin + 50;
      const radarCenterY = yPos + 38;
      const radarRadius = 32;

      drawRadarChart(doc, radarCenterX, radarCenterY, radarRadius, radarData);

      // Draw legend on right side
      const legendX = margin + 105;
      let legendY = yPos + 5;

      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(60, 60, 60);
      doc.text('Détail des scores', legendX, legendY);
      legendY += 6;

      // Legend header
      doc.setFillColor(248, 250, 252);
      doc.rect(legendX, legendY - 3, 75, 6, 'F');
      doc.setFontSize(7);
      doc.setTextColor(100, 100, 100);
      doc.text('Critère', legendX + 2, legendY + 1);
      doc.text('Score', legendX + 55, legendY + 1);
      doc.text('Pond.', legendX + 68, legendY + 1);
      legendY += 7;

      // Legend items
      for (const item of radarData) {
        // Color indicator based on score
        if (item.score >= 70) {
          doc.setFillColor(34, 197, 94); // green
        } else if (item.score >= 40) {
          doc.setFillColor(234, 179, 8); // yellow
        } else {
          doc.setFillColor(239, 68, 68); // red
        }
        doc.circle(legendX + 3, legendY - 1, 1.5, 'F');

        // Criterion name (truncated)
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        let displayName = item.name;
        if (displayName.length > 25) {
          displayName = displayName.substring(0, 24) + '...';
        }
        doc.text(displayName, legendX + 7, legendY);

        // Score
        doc.setFont('helvetica', 'bold');
        doc.text(`${item.score}`, legendX + 55, legendY);

        // Weight
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        doc.text(`${item.weight}%`, legendX + 68, legendY);

        legendY += 5;
      }

      // Color legend
      legendY += 3;
      doc.setFontSize(6);
      doc.setTextColor(100, 100, 100);
      
      doc.setFillColor(34, 197, 94);
      doc.circle(legendX + 3, legendY - 1, 1.2, 'F');
      doc.text('> 70 (Excellent)', legendX + 7, legendY);
      
      doc.setFillColor(234, 179, 8);
      doc.circle(legendX + 35, legendY - 1, 1.2, 'F');
      doc.text('40-70 (Moyen)', legendX + 39, legendY);
      
      doc.setFillColor(239, 68, 68);
      doc.circle(legendX + 63, legendY - 1, 1.2, 'F');
      doc.text('< 40 (Faible)', legendX + 67, legendY);

      yPos += 85;
    }
    if (analysis.resultat_json.criteria_analyses && analysis.resultat_json.criteria_analyses.length > 0) {
      checkNewPage(20);
      
      doc.setTextColor(59, 130, 246);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Analyse détaillée par critère', margin, yPos);
      yPos += 10;

      for (const criteria of analysis.resultat_json.criteria_analyses) {
        checkNewPage(60);

        // Nom du critère avec score
        doc.setFillColor(248, 250, 252);
        doc.rect(margin, yPos, pageWidth - 2 * margin, 10, 'F');
        
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text(criteria.criterion_name || 'Critère', margin + 3, yPos + 7);
        
        const scoreText = `${criteria.criterion_score || 0}/100 (${convertScoreToFive(criteria.criterion_score || 0)}/5)`;
        doc.setFont('helvetica', 'normal');
        doc.text(scoreText, pageWidth - margin - 3, yPos + 7, { align: 'right' });
        
        yPos += 14;

        // Pondération
        if (criteria.weighting || criteria.criterion_weight) {
          doc.setFontSize(9);
          doc.setTextColor(100, 100, 100);
          doc.text(`Pondération: ${criteria.weighting || criteria.criterion_weight}%`, margin + 3, yPos);
          yPos += 6;
        }

        // Éléments trouvés
        if (criteria.found_evidence) {
          checkNewPage(20);
          doc.setFontSize(9);
          doc.setTextColor(59, 130, 246);
          doc.setFont('helvetica', 'bold');
          doc.text('Éléments trouvés:', margin + 3, yPos);
          yPos += 5;
          
          doc.setTextColor(0, 0, 0);
          doc.setFont('helvetica', 'normal');
          const evidenceLines = doc.splitTextToSize(criteria.found_evidence, pageWidth - 2 * margin - 10);
          doc.text(evidenceLines, margin + 5, yPos);
          yPos += evidenceLines.length * 4 + 4;
        }

        // Analyse détaillée
        if (criteria.detailed_analysis) {
          checkNewPage(20);
          doc.setFontSize(9);
          doc.setTextColor(59, 130, 246);
          doc.setFont('helvetica', 'bold');
          doc.text('Analyse détaillée:', margin + 3, yPos);
          yPos += 5;
          
          doc.setTextColor(0, 0, 0);
          doc.setFont('helvetica', 'normal');
          const analysisLines = doc.splitTextToSize(criteria.detailed_analysis, pageWidth - 2 * margin - 10);
          doc.text(analysisLines, margin + 5, yPos);
          yPos += analysisLines.length * 4 + 4;
        }

        // Points forts
        const strengths = ensureArray(criteria.strengths);
        if (strengths.length > 0) {
          checkNewPage(15);
          doc.setFontSize(9);
          doc.setTextColor(34, 197, 94);
          doc.setFont('helvetica', 'bold');
          doc.text('Points forts:', margin + 3, yPos);
          yPos += 5;
          
          doc.setFont('helvetica', 'normal');
          for (const strength of strengths) {
            checkNewPage(8);
            const lines = doc.splitTextToSize(`• ${strength}`, pageWidth - 2 * margin - 15);
            doc.text(lines, margin + 5, yPos);
            yPos += lines.length * 4 + 2;
          }
        }

        // Points faibles
        const weaknesses = ensureArray(criteria.weaknesses);
        if (weaknesses.length > 0) {
          checkNewPage(15);
          doc.setFontSize(9);
          doc.setTextColor(239, 68, 68);
          doc.setFont('helvetica', 'bold');
          doc.text('Points faibles:', margin + 3, yPos);
          yPos += 5;
          
          doc.setFont('helvetica', 'normal');
          for (const weakness of weaknesses) {
            checkNewPage(8);
            const lines = doc.splitTextToSize(`• ${weakness}`, pageWidth - 2 * margin - 15);
            doc.text(lines, margin + 5, yPos);
            yPos += lines.length * 4 + 2;
          }
        }

        // Recommandations
        const recommendations = ensureArray(criteria.recommendations);
        if (recommendations.length > 0) {
          checkNewPage(15);
          doc.setFontSize(9);
          doc.setTextColor(234, 179, 8);
          doc.setFont('helvetica', 'bold');
          doc.text('Recommandations:', margin + 3, yPos);
          yPos += 5;
          
          doc.setFont('helvetica', 'normal');
          for (const rec of recommendations) {
            checkNewPage(8);
            const lines = doc.splitTextToSize(`• ${rec}`, pageWidth - 2 * margin - 15);
            doc.text(lines, margin + 5, yPos);
            yPos += lines.length * 4 + 2;
          }
        }

        yPos += 8;
      }
    }

    // === QUESTIONS D'ENTRETIEN ===
    if (questions.length > 0) {
      checkNewPage(30);
      
      doc.setTextColor(59, 130, 246);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Questions d\'entretien recommandées', margin, yPos);
      yPos += 10;

      for (let i = 0; i < questions.length; i++) {
        const q = questions[i];
        checkNewPage(35);

        // Numéro et critère
        doc.setFillColor(248, 250, 252);
        doc.rect(margin, yPos, pageWidth - 2 * margin, 8, 'F');
        
        doc.setTextColor(59, 130, 246);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text(`Question ${i + 1}`, margin + 3, yPos + 6);
        
        doc.setTextColor(100, 100, 100);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.text(q.critere_associe, pageWidth - margin - 3, yPos + 6, { align: 'right' });
        
        yPos += 12;

        // Question
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        const questionLines = doc.splitTextToSize(q.question, pageWidth - 2 * margin - 10);
        doc.text(questionLines, margin + 3, yPos);
        yPos += questionLines.length * 5 + 3;

        // Objectif
        if (q.objectif) {
          doc.setFontSize(9);
          doc.setTextColor(100, 100, 100);
          doc.setFont('helvetica', 'italic');
          doc.text('Objectif:', margin + 3, yPos);
          yPos += 4;
          
          doc.setFont('helvetica', 'normal');
          const objectifLines = doc.splitTextToSize(q.objectif, pageWidth - 2 * margin - 15);
          doc.text(objectifLines, margin + 5, yPos);
          yPos += objectifLines.length * 4 + 6;
        }
      }
    }

    // === METADONNEES ===
    checkNewPage(35);
    
    doc.setTextColor(100, 100, 100);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Métadonnées', margin, yPos);
    yPos += 6;

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    
    // Modèle LLM utilisé
    const modelUsed = analysis.resultat_json?.analysis_metadata?.model_used || 'Non spécifié';
    doc.text(`Modèle LLM utilisé: ${modelUsed}`, margin, yPos);
    yPos += 4;
    
    doc.text(`Tokens utilisés: ${(analysis.tokens_in + analysis.tokens_out).toLocaleString()} (entrée: ${analysis.tokens_in.toLocaleString()}, sortie: ${analysis.tokens_out.toLocaleString()})`, margin, yPos);
    yPos += 4;
    doc.text(`Coût estimé: CHF ${analysis.cost_estimate.toFixed(4)}`, margin, yPos);

    // Footer
    addPageFooter();

    // Sauvegarde - utiliser le même nom que le header avec la date pour éviter les doublons
    const filename = extractFilename(analysis.depots.fichier_file_id);
    const analysisDate = new Date(analysis.created_at).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).replace(/\//g, '-');
    const pdfFileName = `Analyse de ${filename} - ${analysisDate}.pdf`;
    doc.save(pdfFileName);
  };

  return { exportAnalysisToPdf };
};
